import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level safety net. Without it, any throw during render — most notably a
 * blocked `localStorage` access while bootstrapping in a private/in-app browser
 * — leaves React with nothing mounted, i.e. a blank/black screen. This catches
 * the failure and shows a recoverable fallback instead.
 *
 * Mounted above the providers in index.tsx so it also covers throws raised in
 * their state initializers.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Best-effort: surface the crash in the console for diagnostics. Never
    // rethrow — that would defeat the boundary.
    // eslint-disable-next-line no-console
    console.error('Erreur non gérée capturée par ErrorBoundary:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'system-ui, sans-serif',
          color: '#374151',
          background: '#ffffff',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Une erreur est survenue</h1>
        <p style={{ maxWidth: 420, color: '#6b7280' }}>
          L'application a rencontré un problème inattendu. Réessayez ; si le souci
          persiste, désactivez la navigation privée ou ouvrez le lien dans votre
          navigateur habituel.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '0.6rem 1.2rem',
            borderRadius: 10,
            border: 'none',
            background: '#16a34a',
            color: '#fff',
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
